import { useCallback, useMemo } from "react";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";

/**
 * Resolved variant for experiment 103701 (Direct - Web - Home - Second page view
 * bottom-sheet email signup):
 *   0 = Control              → keep the legacy email opt-in banner + footer signup
 *   1 = "Every visit 2nd page view" → show the popup on the 2nd page view of each visit
 *   2 = "Second visit every page view" → show the popup on every page view of the 2nd+ visit
 */
export type EmailOptInPopupVariant = 0 | 1 | 2;

/**
 * Resolves the experiment variant for the email opt-in popup WITHOUT recording
 * participation, and exposes a `recordParticipation` callback to record later.
 *
 * Participation records at one arm-agnostic moment — the email opt-in slot scrolling into
 * view — for every arm, so the split's denominators stay comparable (see `HomeLayoutBody`
 * and `use-carousel-components`). Popup-arm users swap the banner for the popup at that
 * same moment once their gate is met; control always shows the banner.
 *
 * Call this hook EXACTLY ONCE per render tree (currently from `HomeLayoutBody`)
 * and prop-drill the result.
 *
 * `canParticipate` excludes logged-in users from the experiment split entirely
 * — they would never see the popup, and counting them would dilute the variant
 * lift estimate against control.
 */
export const useEmailOptInPopupVariant = (): {
  variant: EmailOptInPopupVariant;
  recordParticipation: () => void;
} => {
  const appContext = useAppContext();

  // Stable identity for canParticipate so the underlying useFeatureTernary doesn't
  // re-record participation on unrelated renders.
  const canParticipate = useCallback(() => !appContext.isUserLoggedIn, [appContext.isUserLoggedIn]);

  const execVariant = useFeatureTernary(FeatureSetEnum.DiscoveryWebHomeSecondPageViewEmailSignup, canParticipate);
  const recordParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.DiscoveryWebHomeSecondPageViewEmailSignup,
    canParticipate,
  );

  const variant = useMemo<EmailOptInPopupVariant>(
    () =>
      execVariant({
        control: () => 0 as const,
        variant: (_data, value) => (value === 2 ? (2 as const) : (1 as const)),
        record: false,
      }) ?? 0,
    [execVariant],
  );

  return { variant, recordParticipation };
};
